import { signupFormSchema, SignupForm } from '@univdiam/ui/customComponents/SignupForm'
import logo from "@/assets/univdiam.png"
import z from 'zod'
import RightContainer from '@/components/auth/RightContainer'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import api from '@/http/api'
import { OtpForm, otpFormSchema } from '@univdiam/ui/customComponents/OtpVerifyForm'
import { Toaster } from "@univdiam/ui/components/sonner"
import { toast } from 'sonner'
import { maskVerificationId } from '@/lib/maskVerified'
import { useState } from 'react'

const signup = (data: z.infer<typeof signupFormSchema>) => {
  return api.post(`/customer/signup`, data);
}

const verifyRegistration = (data: { verificationId: string, code: string }) => {
  return api.post(`/customer/verify/registration`, data);
}

// channel = sms or phone
const sendOTPtoUser = (data: { verificationId: string, channel: string }) => {
  return api.post(`/customer/send-otp`, data);
}

const Signup = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams();
  const [timeExpired, setTimeExpired] = useState(false)
  const [email, setEmail] = useState('')
  const setQueryParams = (paramsObj: Record<string, string>) => {
    const params = new URLSearchParams(location.search);

    Object.entries(paramsObj).forEach(([key, value]) => {
      params.set(key, value);  // or append if you want duplicate keys
    });

    navigate(`${location.pathname}?${params.toString()}`);
    // { replace: true }
  };


  const { mutate, isError, isPending, error } = useMutation({
    mutationFn: (values: z.infer<typeof signupFormSchema>) => {
      return signup(values)
    },
    onSuccess: async ({ data }) => {
      setQueryParams({
        verification_status: data.verificationStatus,
        verification_id: data.verificationId
      })
    },
    retry: false
  })

  function onSubmit(values: z.infer<typeof signupFormSchema>) {
    const phoneDigits = values.phone_number?.replace(/\D/g, '') ?? ''
    const formattedPhoneNumber = phoneDigits ? `${values.phoneNumberCountryCode}${phoneDigits}` : ''
    const newValues = {
      ...values,
      phone_number: formattedPhoneNumber,
      business_phone_number: `${values.businessPhoneNumberCountryCode}${values.business_phone_number.replace(/\D/g, '')}`
    }
    setEmail(values.email)
    mutate(newValues)
  }


  const verification_status = searchParams.get('verification_status')
  const verificationId = searchParams.get('verification_id')


  const { mutate: verifyOTP, isError: isOTPError, isPending: isOTPPending, error: otpVerifyError, reset: resetVerifyOTP } = useMutation({
    mutationFn: (values: { verificationId: string, code: string }) => {
      return verifyRegistration(values)
    },
    onSuccess: async ({ data }) => {
      console.log('data', data)
      if (data?.success) {
        navigate(`/onboard?name=${data?.data?.customer}&email=${encodeURIComponent(email)}`)
      }
    },
    onError: (err) => {
      // Error is already handled via the isError/error props passed to OtpForm
      console.error('OTP verification failed:', err)
    }
  })

  function onOtpSubmit(values: z.infer<typeof otpFormSchema>) {
    const code = values.otp
    if (verificationId) {
      verifyOTP({
        code,
        verificationId
      })
    }
  }

  const { mutate: sendOTP, isError: isSendOTPError, isPending: isSendOTPPending, error: otpError } = useMutation({
    mutationFn: (values: { verificationId: string, channel: string }) => {
      return sendOTPtoUser(values)
    },
    onSuccess: async () => {
      toast.success(`OTP sent to ${maskVerificationId(verificationId!)}`, {
        position: 'top-center'
      })
      setTimeExpired(false)
      resetVerifyOTP()
    }
  })


  return (
    <div className='grid grid-cols-1 lg:grid-cols-2 min-h-screen'>
      <div className="flex flex-col justify-between min-h-screen p-[20px] lg:p-[40px] items-center w-full">
        {/* Centered content */}
        <div className="flex-1 flex items-center justify-center w-full">
          <div className="w-full max-w-[440px]">
            <img src={logo} alt="Univ Daim" />
            <p className="text-heading2 mt-[32px] lg:mt-[64px]">Welcome to Univ Diam</p>
            <p className="text-heading6 text-secondary mt-[8px] mb-[32px]">
              Fill in your business details to get started.
            </p>
            {
              (verification_status === "pending" && verificationId) ? <OtpForm handleSendOtp={() => sendOTP({
                verificationId: verificationId!,
                channel: 'sms'
              })} onSubmit={onOtpSubmit} isError={isOTPError} isLoading={isOTPPending} isSendOTPError={isSendOTPError} isSendOTPPending={isSendOTPPending} timeExpired={timeExpired} setTimeExpired={setTimeExpired} verification_id={verificationId!} error={otpVerifyError || otpError} /> : <>
                <SignupForm onSubmit={onSubmit} isError={isError} isLoading={isPending} error={error} />
                <p className='text-body2 text-highlight mt-[16px]'>Already have an account? <Link to={'/login'}>
                  <span className='text-[#16111] font-[700] cursor-pointer underline'>Log in</span>
                </Link></p>
              </>
            }

          </div>
        </div>


        <div className="flex flex-col lg:flex-row items-center justify-center gap-[8px] lg:gap-[32px] mt-[70px] lg:mt-[140px]">
          <p className='text-secondary text-caption2'>© 2026 Universal Diamonds. All rights reserved</p>
          <p className='text-caption2 text-[#202020] font-[500]'><Link to={'/'}>Privacy Policy</Link> • <Link to={
            '/'
          }>Terms & Condition</Link></p>
        </div>
      </div>
      <RightContainer />
      <Toaster />
    </div>
  )
}

export default Signup